<template>
    <Modal :show="show" @close="$emit('close')">
        <div class="export-modal-content-body">
            <div class="export-modal-content-body-inner">
                <div class="export-modal-content-body-icon">
                    <icon icon="hero/outline/cloud-download" class="export-modal-content-body-icon__icon"></icon>
                </div>

                <div class="export-modal-content-body__message">
                    <h3 class="export-modal-content-body__title">
                        {{ lang('general.datagrid.export') }}
                    </h3>

                    <div class="export-modal-content-body-fieldsets">
                        <div class="export-modal-content-body-fieldgroup">
                            <h4 class="pb-2 font-semibold">{{ lang('general.export.mode') }}</h4>
                            <label
                                class="export-modal-content-body-fieldgroup__wrapper"
                                :class="[{ 'disabled pointer-events-none opacity-40': overLimit }]"
                            >
                                <AdminRadio v-model="form.mode" :value="'all'" />
                                <span>
                                    {{ lang('general.export.modes.all') }}
                                    <em v-if="overLimit" class="text-xs not-italic opacity-70">
                                        ({{ lang('general.export.over_limit', { limit: limit }) }})
                                    </em>
                                </span>
                            </label>
                            <label
                                class="export-modal-content-body-fieldgroup__wrapper"
                                :class="[{ 'disabled pointer-events-none opacity-40': !hasSelected }]"
                            >
                                <AdminRadio v-model="form.mode" :value="'selected'" />
                                <span>
                                    {{ lang('general.export.modes.selected') }}
                                </span>
                            </label>
                            <label
                                v-if="hasFilters"
                                class="export-modal-content-body-fieldgroup__wrapper"
                                :class="[{ 'disabled pointer-events-none opacity-40': overLimit }]"
                            >
                                <AdminRadio v-model="form.mode" :value="'filtered'" />
                                <span>
                                    {{ lang('general.export.modes.filtered') }}
                                    <em v-if="overLimit" class="text-xs not-italic opacity-70">
                                        ({{ lang('general.export.over_limit', { limit: limit }) }})
                                    </em>
                                </span>
                            </label>
                        </div>

                        <div class="export-modal-content-body-fieldgroup">
                            <h4 class="export-modal-content-body-fieldgroup__label">
                                {{ lang('general.export.format') }}
                            </h4>
                            <AdminSelect v-model="form.format" :options="formatOptionsRef"></AdminSelect>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="export-modal-content-footer">
            <Button color="default" @click="$emit('close')">
                {{ lang('general.close') }}
            </Button>
            <Button :href="href" :a="true">
                {{ lang('general.datagrid.export') }}
            </Button>
        </div>
    </Modal>
</template>

<script setup lang="ts">
import { ref, computed, toRef, watch } from 'vue';

interface FormatOption {
    id: string;
    name: string;
}

const props = withDefaults(
    defineProps<{
        show: boolean;
        url: string;
        selected?: number[];
        formatOptions?: FormatOption[];
        defaultMode?: 'all' | 'selected' | 'filtered';
        filters?: object[];
        search?: string;
        hasActiveFilters?: boolean;
        limit?: number | null;
        total?: number;
    }>(),
    {
        selected: () => [],
        formatOptions: () => [
            { id: 'xls', name: 'XLS' },
            { id: 'csv', name: 'CSV' },
            { id: 'json', name: 'JSON' },
            { id: 'xml', name: 'XML' },
        ],
        defaultMode: 'all',
        filters: () => [],
        search: '',
        hasActiveFilters: false,
        limit: null,
        total: 0,
    },
);

defineEmits<{
    close: [];
}>();

const form = ref({
    mode: props.defaultMode,
    format: 'xls',
});

const formatOptionsRef = toRef(props, 'formatOptions');

const urlSearch = ref('');
const urlFilters = ref<object[]>([]);

const hasFilters = computed(() => {
    if (props.hasActiveFilters) return true;
    return urlSearch.value !== '' || urlFilters.value.length > 0;
});

const effectiveFilters = computed(() => {
    if (props.filters && props.filters.length > 0) return props.filters;
    return urlFilters.value;
});

const effectiveSearch = computed(() => {
    if (props.search) return props.search;
    return urlSearch.value;
});

const href = computed(() => {
    let url = `${props.url}?mode=${form.value.mode}&format=${form.value.format}&selected=${props.selected.join(',')}`;
    if (form.value.mode === 'filtered') {
        if (effectiveFilters.value.length > 0) {
            url += `&filters=${encodeURIComponent(JSON.stringify(effectiveFilters.value))}`;
        }
        if (effectiveSearch.value) {
            url += `&search=${encodeURIComponent(effectiveSearch.value)}`;
        }
    }
    return url;
});

const hasSelected = computed(() => props.selected.length > 0);

// pagination.total reflects the active search/filters, so it describes the "filtered"
// mode when filters are on, else "all"; all-count is always >= filtered-count, so an
// over-limit total blocks both. Backend re-checks and refuses regardless.
const overLimit = computed(() => props.limit !== null && props.total > props.limit);

// Snapshot URL params and reset form when modal opens
watch(
    () => props.show,
    (isVisible) => {
        if (isVisible) {
            const query = new URLSearchParams(window.location.search);
            urlSearch.value = query.get('search') ?? '';
            const rawFilters = query.get('filters');
            if (rawFilters) {
                try { urlFilters.value = JSON.parse(rawFilters); } catch { urlFilters.value = []; }
            } else {
                urlFilters.value = [];
            }

            form.value.mode = hasFilters.value ? 'filtered' : props.defaultMode;
            form.value.format = 'xls';

            if (overLimit.value && form.value.mode !== 'selected' && hasSelected.value) {
                form.value.mode = 'selected';
            }
        }
    },
);
</script>
